/**
 * Label Content Type Icon Mapping
 *
 * Maps content types to their corresponding design system icons
 * for use in moderation label UI components
 */

import { Chat, Edit, Photo, Profile } from '@soteria/design-system-ve';
import type { ComponentType } from 'react';

/**
 * Gets the icon component for a content type
 *
 * Returns appropriate design system icon component based on content type:
 * - TEXT -> Chat icon
 * - IMAGE -> Photo icon
 * - USERNAME -> Profile icon
 * - CUSTOM -> Edit icon
 *
 * @param contentType - The content type string (e.g., 'Text', 'Image') or null
 * @returns Icon component or null if not found
 *
 * @example
 * ```tsx
 * const IconComponent = getContentTypeIconComponent('Text');
 * return IconComponent ? <Icon icon={IconComponent} /> : null;
 * ```
 */
export function getContentTypeIconComponent(contentType: string | null): ComponentType | null {
  if (!contentType) {
    return null;
  }

  // Normalize content type to uppercase for comparison
  const normalizedType = contentType.toUpperCase();

  switch (normalizedType) {
    case 'TEXT':
      return Chat;
    case 'IMAGE':
      return Photo;
    case 'USERNAME':
      return Profile;
    case 'CUSTOM':
      return Edit;
    default:
      return null;
  }
}
